import React, { useEffect, useState, useRef, useCallback } from "react";
import {
  Autocomplete,
  Button,
  Divider,
  Grid,
  Paper,
  TextField,
  Typography,
  IconButton,
  CircularProgress,
  InputAdornment,
  Tooltip,
  Box,
  Stack,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SearchIcon from "@mui/icons-material/Search";
import useFetchObjects from "../../../../api/useFetchObjects";
import useAdd from "../../../../api/useAdd";
import { useAuth } from "../../../../context";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { getLocalDateString } from "../../../../utils";

// Debounce function
function debounce(func, delay) {
  let timeout;
  return function (...args) {
    const context = this;
    clearTimeout(timeout);
    timeout = setTimeout(() => func.apply(context, args), delay);
  };
}

const EchoReferral = () => {
  const { t } = useTranslation();
  const auth = useAuth();
  const token = auth?.user?.token;
  const navigate = useNavigate();

  const { handleAdd, resetForm } = useAdd("patient-echo", token);

  const today = getLocalDateString();
  const [ticketSearch, setTicketSearch] = useState("");
  const selectedTicketSnapshot = useRef(null);

  // Pagination and search state for doctors
  const [doctorPage, setDoctorPage] = useState(1);
  const [doctorPageSize, setDoctorPageSize] = useState(100);
  const [doctorSearchTerm, setDoctorSearchTerm] = useState("");
  const [allDoctors, setAllDoctors] = useState([]);
  const [hasMoreDoctors, setHasMoreDoctors] = useState(true);
  const [doctorInputValue, setDoctorInputValue] = useState("");

  const { data: echoTypesData } = useFetchObjects(
    ["echo-types"],
    "echo-types/",
    token
  );

  const effectiveTicketSearch = selectedTicketSnapshot.current ? "" : ticketSearch.trim();
  const shouldSearchTickets = effectiveTicketSearch.length >= 3;
  const { data: ticketsData, isLoading: isLoadingTickets } = useFetchObjects(
    ["tickets-echo-reception", effectiveTicketSearch],
    `tickets/?search=${encodeURIComponent(effectiveTicketSearch)}&page_size=200`,
    token,
    { enabled: !!token && shouldSearchTickets }
  );
  const tickets = shouldSearchTickets ? (ticketsData?.results ?? (Array.isArray(ticketsData) ? ticketsData : [])) : [];

  const {
    data: doctorsData,
    isLoading: isLoadingDoctors,
    refetch: refetchDoctors,
  } = useFetchObjects(
    ["users", doctorPage, doctorPageSize, doctorSearchTerm],
    `users/?page=${doctorPage}&page_size=${doctorPageSize}&search=${doctorSearchTerm}`,
    token
  );

  const echoTypes = echoTypesData || [];

  const [echoDetails, setEchoDetails] = useState({
    echoType: "",
    testDate: getLocalDateString(),
    isUrgent: false,
    totalAmount: "",
    ticket: null,
    doctor: null,
  });

  const [errors, setErrors] = useState({});

  // Debounced search for doctors
  const debouncedSetDoctorSearchTerm = useCallback(
    debounce((value) => {
      setDoctorSearchTerm(value);
    }, 300),
    []
  );

  // Handle doctors data changes and pagination
  useEffect(() => {
    // Check if data exists and is paginated
    if (doctorsData?.results && Array.isArray(doctorsData.results)) {
      // If we're on page 1, replace the list
      // Otherwise, append for pagination
      if (doctorPage === 1) {
        setAllDoctors(doctorsData.results);
      } else {
        setAllDoctors((prevDoctors) => {
          const newDoctors = [...prevDoctors, ...doctorsData.results];
          // Remove duplicates based on doctor ID
          const uniqueDoctors = newDoctors.reduce((acc, current) => {
            if (!acc.find((item) => item.id === current.id)) {
              acc.push(current);
            }
            return acc;
          }, []);
          return uniqueDoctors;
        });
      }
      setHasMoreDoctors(doctorsData.next !== null);
    } else if (doctorsData && Array.isArray(doctorsData)) {
      // Handle case where API returns array directly (no pagination)
      if (doctorPage === 1) {
        setAllDoctors(doctorsData);
      } else {
        setAllDoctors((prevDoctors) => {
          const newDoctors = [...prevDoctors, ...doctorsData];
          const uniqueDoctors = newDoctors.reduce((acc, current) => {
            if (!acc.find((item) => item.id === current.id)) {
              acc.push(current);
            }
            return acc;
          }, []);
          return uniqueDoctors;
        });
      }
      setHasMoreDoctors(false);
    } else if (!doctorsData) {
      // No data yet, keep current state
      return;
    } else {
      setHasMoreDoctors(false);
    }
  }, [doctorsData, doctorPage]);

  // Handle search term changes for doctors - reset list and page
  useEffect(() => {
    setAllDoctors([]);
    setDoctorPage(1);
    setHasMoreDoctors(true);
    // React Query will automatically refetch when doctorSearchTerm changes
    // because it's in the queryKey array
  }, [doctorSearchTerm]);

  // Reset form when resetForm prop changes
  useEffect(() => {
    if (resetForm) {
      setEchoDetails({
        echoType: "",
        testDate: getLocalDateString(),
        isUrgent: false,
        totalAmount: "",
        ticket: null,
        doctor: null,
      });
      setErrors({});
      setDoctorInputValue("");
      selectedTicketSnapshot.current = null;
    }
  }, [resetForm]);

  // Handle doctor input change with debounce
  const handleDoctorInputChange = (event, newInputValue) => {
    setDoctorInputValue(newInputValue);
    debouncedSetDoctorSearchTerm(newInputValue);
  };

  // Handle doctor selection
  const handleDoctorChange = (event, newValue) => {
    setEchoDetails({ ...echoDetails, doctor: newValue });
  };

  // Handle infinite scroll for doctors
  const handleDoctorScroll = (event) => {
    const { scrollTop, clientHeight, scrollHeight } = event.target;
    if (
      scrollHeight - scrollTop <= clientHeight + 50 &&
      hasMoreDoctors &&
      !isLoadingDoctors
    ) {
      setDoctorPage((prevPage) => prevPage + 1);
    }
  };

  // Handle form field changes
  const handleChange = (event) => {
    const { name, value, checked, type } = event.target;
    setEchoDetails((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));

    if (name === "echoType") {
      const selectedType = echoTypes.find(
        (type) => type.id.toString() === value
      );
      setEchoDetails((prev) => ({
        ...prev,
        totalAmount: selectedType ? selectedType.cost?.toString() || "" : "",
      }));
    }
  };

  // Handle form submission
  const handleSubmit = () => {
    const { ticket, doctor, echoType, testDate, totalAmount } = echoDetails;
    const newErrors = {};

    if (!ticket) {
      newErrors.ticket = t("doctor.selectTicketRequired");
    }
    if (!doctor) {
      newErrors.doctor = t("doctor.doctorRequired");
    }
    if (!echoType) {
      newErrors.echoType = t("doctor.echoTypeRequired");
    }
    if (!testDate) {
      newErrors.testDate = t("doctor.testDateRequired");
    }
    if (!totalAmount) {
      newErrors.totalAmount = t("doctor.totalAmountRequired");
    }

    setErrors(newErrors);
    if (Object.keys(newErrors).length === 0) {
      const patientId = typeof ticket.patient === "object" ? ticket.patient?.id : ticket.patient;
      const payload = {
        patient: patientId,
        ticket: ticket.id,
        doctor: doctor.id,
        echo_type: echoType,
        date: testDate,
        is_urgent: echoDetails.isUrgent,
        total_amount: totalAmount,
      };
      handleAdd(payload);
    }
  };

  return (
    <Paper
      sx={{
        boxShadow: "0px 8px 24px rgba(0, 0, 0, 0.1)",
        borderRadius: "16px",
        mt: 4,
        mb: 4,
        p: 6,
      }}
    >
      <Box display="flex" alignItems="center" mb={4}>
        <Tooltip title={t("reception.goBack")} arrow>
          <IconButton
            onClick={() => navigate(-1)}
            aria-label={t("reception.goBack")}
            sx={{ mr: 2 }}
          >
            <ArrowBackIcon />
          </IconButton>
        </Tooltip>
        <Typography fontWeight={600} variant="h4" color="primary" gutterBottom>
          {t("doctor.echoReferralForm")}
        </Typography>
      </Box>

      <Grid container spacing={4}>
        {/* Ticket selection – patient comes from ticket */}
        <Grid item xs={12} md={6}>
          <Autocomplete
            options={tickets}
            noOptionsText={shouldSearchTickets ? "No options" : "Type at least 3 characters to search"}
            getOptionLabel={(option) =>
              option ? `${option.ticket_number || option.id}${option.patient_name_display ? ` - ${option.patient_name_display}` : ""}` : ""
            }
            value={echoDetails.ticket}
            inputValue={ticketSearch}
            onInputChange={(e, value) => {
              setTicketSearch(value);
              if (!value) selectedTicketSnapshot.current = null;
            }}
            onChange={(event, newValue) => {
              selectedTicketSnapshot.current = newValue ? { id: newValue.id } : null;
              setEchoDetails((prev) => ({ ...prev, ticket: newValue }));
              setErrors((prev) => ({ ...prev, ticket: "" }));
            }}
            loading={isLoadingTickets}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t("print.ticketNumber")}
                placeholder={t("doctor.selectTicketPlaceholder")}
                variant="outlined"
                fullWidth
                error={!!errors.ticket}
                helperText={errors.ticket}
                InputProps={{
                  ...params.InputProps,
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <React.Fragment>
                      {isLoadingTickets ? <CircularProgress color="inherit" size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </React.Fragment>
                  ),
                }}
              />
            )}
            isOptionEqualToValue={(option, value) => option?.id === value?.id}
          />
        </Grid>
        {echoDetails.ticket && (
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label={t("laboratory.patientName")}
              value={echoDetails.ticket.patient_name_display ?? ""}
              variant="outlined"
              InputProps={{ readOnly: true }}
            />
          </Grid>
        )}

        {/* Doctor Autocomplete */}
        <Grid item xs={12} md={6}>
          <Autocomplete
            options={allDoctors}
            getOptionLabel={(option) =>
              `${option.first_name} ${option.last_name}`.trim()
            }
            inputValue={doctorInputValue}
            onInputChange={handleDoctorInputChange}
            onChange={handleDoctorChange}
            value={echoDetails.doctor}
            ListboxProps={{
              onScroll: handleDoctorScroll,
            }}
            loading={isLoadingDoctors}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t("doctor.selectDoctor")}
                variant="outlined"
                fullWidth
                InputProps={{
                  ...params.InputProps,
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <React.Fragment>
                      {isLoadingDoctors ? (
                        <CircularProgress color="inherit" size={20} />
                      ) : null}
                      {params.InputProps.endAdornment}
                    </React.Fragment>
                  ),
                }}
              />
            )}
          />
        </Grid>

        {/* Echo Type */}
        <Grid item xs={12} sm={6}>
          <TextField
            select
            fullWidth
            label={t("doctor.selectEchoType")}
            value={echoDetails.echoType}
            onChange={handleChange}
            name="echoType"
            variant="outlined"
            SelectProps={{
              native: true,
            }}
          >
            <option value=""></option>
            {echoTypes.map((type) => (
              <option key={type.id} value={type.id}>
                {type.name}
              </option>
            ))}
          </TextField>
        </Grid>

        {/* Test Date */}
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label={t("doctor.testDate")}
            type="date"
            name="testDate"
            value={echoDetails.testDate}
            onChange={handleChange}
            InputLabelProps={{
              shrink: true,
            }}
            variant="outlined"
          />
        </Grid>

        {/* Total Amount */}
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label={t("doctor.totalAmount")}
            type="number"
            name="totalAmount"
            value={echoDetails.totalAmount}
            onChange={handleChange}
            variant="outlined"
            inputProps={{
              min: 0,
              step: "0.01",
            }}
          />
        </Grid>

        {/* Urgent Switch */}
        <Grid item xs={12} sm={6}>
          <TextField
            select
            fullWidth
            label={t("doctor.isUrgent")}
            name="isUrgent"
            value={echoDetails.isUrgent}
            onChange={handleChange}
            variant="outlined"
            SelectProps={{
              native: true,
            }}
          >
            <option value={false}>{t("doctor.no")}</option>
            <option value={true}>{t("doctor.yes")}</option>
          </TextField>
        </Grid>

        {/* Submit Button */}
        <Grid item xs={12}>
          <Stack direction="row" justifyContent="flex-end" spacing={2}>
            <Button
              variant="contained"
              onClick={handleSubmit}
              sx={{
                fontWeight: 700,
                backgroundColor: "primary.main",
                "&:hover": {
                  backgroundColor: "primary.dark",
                },
              }}
            >
              {t("doctor.submit")}
            </Button>
          </Stack>
        </Grid>
      </Grid>
    </Paper>
  );
};

export default EchoReferral;




